import type { Ref } from 'react'
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import styled from 'styled-components'
import type { CaptchaProvider } from 'api/auth'
import config from 'constants/config'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import noop from 'lib/function/noop'
import { loadTurnstile, type Turnstile } from 'lib/turnstile/loadTurnstile'
import { reportLog } from 'services/userMonitoring'

type TurnstileAction = 'account_lookup' | 'login'

const TURNSTILE_UNSUPPORTED = 'Turnstile does not support this browser'

export interface LoginCaptchaHandle {
  provider: CaptchaProvider
  executeAsync: () => Promise<string | null>
}

interface Props {
  // Turnstile only: svc-auth checks the token's action per endpoint. The reCAPTCHA arm sends no action, as before.
  turnstileAction: TurnstileAction
  ref: Ref<LoginCaptchaHandle>
}

interface PendingToken {
  resolve: (token: string) => void
  reject: (error: Error) => void
}

// Sits between the form's field and its submit button but stays out of the layout, so it adds no gap, until Cloudflare
// needs the visitor to interact. The account lookup waits for the token inside its API request, while the login modal's
// loading overlay (z-index 1) covers the form, so the slot stacks above it to keep the checkbox clickable.
const CaptchaSlot = styled.div`
  position: absolute;
  z-index: 2;

  &.interactive {
    position: relative;
    width: 100%;
  }
`

// Logged to Datadog so client-side Turnstile failures in the variant can be compared with svc-auth's "Turnstile failed" logs.
function reportTurnstileFailure(turnstileAction: TurnstileAction, reason: string) {
  reportLog('Turnstile failed', { turnstileAction, reason }, 'warn')
}

/** The login form's captcha: invisible reCAPTCHA, or Cloudflare Turnstile in the reCAPTCHA vs Turnstile experiment's variant. */
function LoginCaptcha({ turnstileAction, ref }: Props) {
  const canUseTurnstile = Boolean(config.TURNSTILE_SITE_KEY)
  // Only enrol visitors who can get the Turnstile arm, so a stack without the site key adds no one to the experiment.
  const { variation } = useExperiment(ChertoExperiments.loginCaptchaRecaptchaVsTurnstile, { canActivate: canUseTurnstile })
  const provider: CaptchaProvider = canUseTurnstile && variation === 'variant' ? 'turnstile' : 'recaptcha'

  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | undefined>(undefined)
  const pendingTokenRef = useRef<PendingToken | undefined>(undefined)
  const usedTokenRef = useRef<string | undefined>(undefined)
  const failedRef = useRef(false)
  const unsupportedRef = useRef(false)
  // Not cleared when the challenge leaves interactive mode, because the solved widget can stay on screen and must not
  // overlap the button.
  const [interactive, setInteractive] = useState(false)

  const renderWidget = useCallback((turnstile: Turnstile) => {
    if (widgetIdRef.current || !containerRef.current) return widgetIdRef.current

    widgetIdRef.current = turnstile.render(containerRef.current, {
      sitekey: config.TURNSTILE_SITE_KEY,
      action: turnstileAction,
      // Most visitors never see the widget. One that Cloudflare wants to challenge gets a checkbox in the form.
      appearance: 'interaction-only',
      // The default 'auto' follows the visitor's OS colour scheme, which would put a dark widget in the light login form.
      theme: 'light',
      // Spans the slot's width, matching the full-width field and button around it. Cloudflare keeps a 300px minimum.
      size: 'flexible',
      // The default auto retry can call error-callback on every attempt. 'never' makes the first error final, and the
      // next submit resets the widget for a fresh challenge.
      retry: 'never',
      callback: (token) => {
        failedRef.current = false
        pendingTokenRef.current?.resolve(token)
        pendingTokenRef.current = undefined
      },
      'before-interactive-callback': () => setInteractive(true),
      'error-callback': (errorCode) => {
        failedRef.current = true
        reportTurnstileFailure(turnstileAction, `error ${errorCode}`)
        pendingTokenRef.current?.reject(new Error(`Turnstile error ${errorCode}`))
        pendingTokenRef.current = undefined
        return true
      },
      // An unsupported browser never gets a token, so without this the submit would wait forever.
      'unsupported-callback': () => {
        unsupportedRef.current = true
        reportTurnstileFailure(turnstileAction, 'unsupported browser')
        pendingTokenRef.current?.reject(new Error(TURNSTILE_UNSUPPORTED))
        pendingTokenRef.current = undefined
      },
    })
    return widgetIdRef.current
  }, [turnstileAction])

  // Render when the form opens, so most visitors hold a token before they continue, and a visitor Cloudflare wants to
  // check sees the checkbox while the form is still usable. A load failure here is retried and logged on submit.
  useEffect(() => {
    if (provider !== 'turnstile') return
    let unmounted = false
    loadTurnstile().then((turnstile) => {
      if (!unmounted) renderWidget(turnstile)
    }).catch(noop)

    return () => {
      unmounted = true
      if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current)
      widgetIdRef.current = undefined
      // A removed widget never calls back. The account lookup waits for the token inside its API request, so an
      // unsettled token would leave the login modal processing when it is reopened.
      pendingTokenRef.current?.reject(new Error('Turnstile widget removed before issuing a token'))
      pendingTokenRef.current = undefined
    }
  }, [provider, renderWidget])

  useImperativeHandle(ref, () => ({
    provider,
    executeAsync: async() => {
      if (provider === 'recaptcha') return recaptchaRef.current?.executeAsync() ?? null

      const turnstile = await loadTurnstile().catch((error: unknown) => {
        reportTurnstileFailure(turnstileAction, error instanceof Error ? error.message : 'script load failed')
        throw error
      })
      const widgetId = renderWidget(turnstile)
      if (!widgetId) return null
      if (unsupportedRef.current) throw new Error(TURNSTILE_UNSUPPORTED)

      const token = turnstile.getResponse(widgetId)
      const expired = turnstile.isExpired(widgetId)
      // svc-auth validates a token only once, so a token already sent is never returned again.
      if (token && token !== usedTokenRef.current && !expired) {
        usedTokenRef.current = token
        return token
      }

      const nextToken = new Promise<string>((resolve, reject) => {
        pendingTokenRef.current = { resolve, reject }
      })
      // A used or expired token, or an errored widget, gets no new callback until the widget runs a fresh challenge.
      // Otherwise the challenge is still running, possibly waiting for the checkbox, and resetting would restart it.
      if (token || expired || failedRef.current) turnstile.reset(widgetId)

      const freshToken = await nextToken
      usedTokenRef.current = freshToken
      return freshToken
    },
  }), [provider, turnstileAction, renderWidget])

  if (provider === 'turnstile') {
    return <CaptchaSlot ref={containerRef} className={interactive ? 'interactive' : undefined} data-testid="captcha-slot" />
  }
  if (!config.RECAPTCHA_KEY) return null
  return <CaptchaSlot data-testid="captcha-slot">
    <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={recaptchaRef} />
  </CaptchaSlot>
}

export default LoginCaptcha
